import type { TonAsset } from '../api/dto/offers.dto.js';

/**
 * The one screen a crypto purchase lives in: pick the asset, connect, sign,
 * wait for the chain, see the result. A DOM overlay above the game canvases
 * (TON Connect's own modal is DOM as well). Copy is deliberately plain
 * English for v1; localisation keys can replace the strings later.
 */

export type SheetState =
  | { kind: 'choose_asset'; prices: { asset: TonAsset; label: string }[]; usd?: string }
  | { kind: 'connecting' }
  | { kind: 'signing'; amount: string; valid_until_ms: number; gas_note?: boolean }
  | { kind: 'sent'; amount: string }
  | { kind: 'payment_seen'; explorer_url?: string }
  | { kind: 'granting' }
  | { kind: 'underpaid'; order_id: string; received: string; amount: string }
  | { kind: 'success'; granted?: string }
  | { kind: 'error'; title: string; detail?: string; retry?: boolean }
  | { kind: 'needs_support'; order_id: string; explorer_url?: string }
  | { kind: 'wallet'; address: string; testnet?: boolean };

export type SheetAction = 'retry' | 'close' | 'disconnect';

const SHEET_ID = 'tonPaymentSheet';

let root: HTMLDivElement | null = null;
let countdown_timer: number | null = null;
let on_choose: ( ( asset: TonAsset | null ) => void ) | null = null;
let on_action: ( ( action: SheetAction ) => void ) | null = null;
let on_cancel: ( () => void ) | null = null;

// Below TON Connect's own modal (z-index 1000): while the wallet picker or the
// confirmation modal is open it must sit on top and receive the clicks; above
// the game's canvases (≤ 999, the FPS counter) otherwise.
const SHEET_Z_INDEX = 990;

function ensureRoot(): HTMLDivElement
{
  if ( root && document.body.contains( root ) ) return root;
  root = document.createElement( 'div' );
  root.id = SHEET_ID;
  root.setAttribute( 'style', [
    'position:fixed', 'inset:0', `z-index:${SHEET_Z_INDEX}`, 'display:none', 'align-items:flex-end', 'justify-content:center',
    'background:rgba(8,10,20,.72)', 'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif', 'color:#f1f5f9',
  ].join( ';' ) );
  root.addEventListener( 'click', ( event ) => { if ( event.target === root ) handleClose(); } );
  document.body.appendChild( root );
  return root;
}

function handleClose(): void
{
  if ( on_choose ) { const cb = on_choose; on_choose = null; cb( null ); }
  if ( on_action ) { const cb = on_action; on_action = null; cb( 'close' ); }
  if ( on_cancel ) { const cb = on_cancel; on_cancel = null; cb(); }
  TonPaymentSheet.close();
}

function esc( value: string ): string
{
  return value.replace( /[&<>"']/g, ( ch ) => ( { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string ) );
}

function card( title: string, body: string, footer = '' ): string
{
  return `<div style="width:min(440px,100vw);max-height:90vh;overflow:auto;background:#141a2b;border-radius:18px 18px 0 0;padding:20px 20px calc(20px + env(safe-area-inset-bottom));box-shadow:0 -8px 40px rgba(0,0,0,.5)">
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px"><div style="font-size:17px;font-weight:700">${esc( title )}</div><button data-ton-close aria-label="Close" style="background:none;border:none;color:#94a3b8;font-size:22px;cursor:pointer">×</button></div>
    <div style="font-size:14px;line-height:1.45;color:#cbd5e1">${body}</div>
    ${footer ? `<div style="margin-top:16px;display:flex;flex-direction:column;gap:10px">${footer}</div>` : ''}
  </div>`;
}

function button( attr: string, label: string, primary = true ): string
{
  const style = primary
    ? 'background:#0098ea;color:#fff;border:none'
    : 'background:#1f2937;color:#e2e8f0;border:1px solid #334155';
  return `<button ${attr} style="${style};border-radius:12px;padding:13px 16px;font-size:15px;font-weight:600;cursor:pointer;width:100%">${label}</button>`;
}

function spinner(): string
{
  return `<div style="display:flex;justify-content:center;margin:12px 0"><div style="width:28px;height:28px;border:3px solid #334155;border-top-color:#0098ea;border-radius:50%;animation:tonspin 1s linear infinite"></div></div><style>@keyframes tonspin{to{transform:rotate(360deg)}}</style>`;
}

function render( state: SheetState ): string
{
  switch ( state.kind )
  {
    case 'choose_asset':
      return card( 'Pay with crypto', `Choose what to pay with.${state.usd ? ` The price is ${esc( state.usd )}.` : ''}<div style="margin-top:8px;font-size:12px;color:#94a3b8">USDT payments need about 0.05 TON in the wallet for the network fee; the unused part comes back. Items are delivered immediately, so by paying you waive the right of withdrawal.</div>`,
        state.prices.map( ( price ) => button( `data-ton-asset="${price.asset}"`, `Pay ${esc( price.label )}` ) ).join( '' ) + button( 'data-ton-close', 'Cancel', false ) );
    case 'connecting':
      return card( 'Connect a wallet', `Pick a wallet in the window that opened. On a phone, scan the QR code with the wallet app, not the camera.${spinner()}`, button( 'data-ton-close', 'Cancel', false ) );
    case 'signing':
      // No Cancel of our own here: TON Connect's confirmation modal sits above this sheet and has no
      // public close API; closing it rejects the request as "Transaction was not sent" (= cancelled).
      return card( 'Confirm in your wallet', `Your wallet will show <b>${esc( state.amount )}</b>${state.gas_note ? ' plus about 0.05 TON for the network fee (the unused part comes back)' : ' plus a small network fee'}. Confirm it there, or close the wallet window to cancel.<div data-ton-countdown style="margin-top:8px;font-size:12px;color:#94a3b8"></div>${spinner()}` );
    case 'sent':
      return card( 'Waiting for the network', `Your wallet sent <b>${esc( state.amount )}</b>. This usually takes a few seconds. You can go back to the game; the purchase completes on its own.${spinner()}`, button( 'data-ton-close', 'Back to the game', false ) );
    case 'payment_seen':
      return card( 'Payment found', `Your payment is in the network and is being confirmed. Your funds are safe.${state.explorer_url ? ` <a href="${esc( state.explorer_url )}" target="_blank" rel="noopener" style="color:#38bdf8">View transaction</a>` : ''}${spinner()}`, button( 'data-ton-close', 'Back to the game', false ) );
    case 'granting':
      return card( 'Almost there', `Payment confirmed, delivering your items.${spinner()}` );
    case 'underpaid':
      return card( 'Payment incomplete', `We received <b>${esc( state.received )}</b> of <b>${esc( state.amount )}</b>. Please contact support with this order number and we will complete or refund it:<div style="margin-top:8px;font:13px ui-monospace,monospace;background:#0b1020;padding:8px 10px;border-radius:8px;user-select:all">${esc( state.order_id )}</div>`, button( 'data-ton-action="copy"', 'Copy order number', false ) + button( 'data-ton-close', 'Close', false ) );
    case 'success':
      return card( 'Purchase complete', `Thank you! ${state.granted ? esc( state.granted ) + ' added to your account.' : 'Your items were added to your account.'}`, button( 'data-ton-close', 'Great' ) );
    case 'error':
      return card( state.title, esc( state.detail ?? '' ), ( state.retry ? button( 'data-ton-action="retry"', 'Try again' ) : '' ) + button( 'data-ton-close', 'Close', false ) );
    case 'wallet':
      return card( 'Connected wallet', `Crypto purchases are signed with this wallet.<div style="margin-top:8px;font:13px ui-monospace,monospace;background:#0b1020;padding:8px 10px;border-radius:8px;user-select:all;word-break:break-all">${esc( state.address )}</div>${state.testnet ? '<div style="margin-top:6px;font-size:12px;color:#fbbf24">Testnet wallet</div>' : ''}`,
        button( 'data-ton-action="disconnect"', 'Disconnect', false ) + button( 'data-ton-close', 'Close' ) );
    case 'needs_support':
      return card( 'We need to check this', `Your payment could not be matched automatically. Please contact support with this order number:<div style="margin-top:8px;font:13px ui-monospace,monospace;background:#0b1020;padding:8px 10px;border-radius:8px;user-select:all">${esc( state.order_id )}</div>${state.explorer_url ? `<div style="margin-top:6px"><a href="${esc( state.explorer_url )}" target="_blank" rel="noopener" style="color:#38bdf8">View transaction</a></div>` : ''}`, button( 'data-ton-action="copy"', 'Copy order number', false ) + button( 'data-ton-close', 'Close', false ) );
  }
}

function bind( state: SheetState ): void
{
  const container = ensureRoot();
  for ( const element of container.querySelectorAll( '[data-ton-close]' ) ) element.addEventListener( 'click', handleClose );
  for ( const element of container.querySelectorAll( '[data-ton-asset]' ) )
  {
    element.addEventListener( 'click', () =>
    {
      const asset = ( element as HTMLElement ).dataset.tonAsset as TonAsset;
      if ( on_choose ) { const cb = on_choose; on_choose = null; cb( asset ); }
    } );
  }
  for ( const element of container.querySelectorAll( '[data-ton-action]' ) )
  {
    element.addEventListener( 'click', async () =>
    {
      const action = ( element as HTMLElement ).dataset.tonAction;
      if ( action === 'copy' && ( state.kind === 'needs_support' || state.kind === 'underpaid' ) )
      {
        try { await navigator.clipboard.writeText( state.order_id ); } catch { /* clipboard blocked */ }
        return;
      }
      if ( on_action && ( action === 'retry' || action === 'disconnect' ) ) { const cb = on_action; on_action = null; cb( action ); }
    } );
  }
  if ( state.kind === 'signing' )
  {
    const target = container.querySelector( '[data-ton-countdown]' );
    const tick = () =>
    {
      const left = Math.max( 0, Math.round( ( state.valid_until_ms - Date.now() ) / 1000 ) );
      if ( target ) target.textContent = left > 0 ? `The request is valid for ${left}s.` : 'The request expired. Close the wallet window and try again.';
    };
    tick();
    countdown_timer = window.setInterval( tick, 1000 );
  }
}

export const TonPaymentSheet = {
  isOpen(): boolean
  {
    return !!root && root.style.display !== 'none';
  },

  show( state: SheetState ): void
  {
    if ( countdown_timer ) { clearInterval( countdown_timer ); countdown_timer = null; }
    if ( state.kind !== 'connecting' && state.kind !== 'signing' ) on_cancel = null;
    const container = ensureRoot();
    container.innerHTML = render( state );
    container.style.display = 'flex';
    bind( state );
  },

  close(): void
  {
    if ( countdown_timer ) { clearInterval( countdown_timer ); countdown_timer = null; }
    on_choose = null;
    on_action = null;
    on_cancel = null;
    if ( root ) root.style.display = 'none';
  },

  /**
   * What "Cancel" / tapping outside means while a wallet round-trip is in
   * flight (connecting, signing): the flow is aborted, not merely hidden.
   * Cleared by the next show() of a non-cancellable state and by close().
   */
  onCancel( cb: ( () => void ) | null ): void
  {
    on_cancel = cb;
  },

  /** Shows the asset picker; resolves with the chosen asset or null when dismissed. */
  chooseAsset( prices: { asset: TonAsset; label: string }[], usd?: string ): Promise<TonAsset | null>
  {
    return new Promise( ( resolve ) =>
    {
      on_choose = resolve;
      this.show( { kind: 'choose_asset', prices, usd } );
    } );
  },

  /** Resolves with the action the player pressed on the current state ('close' when dismissed). */
  waitForAction(): Promise<SheetAction>
  {
    return new Promise( ( resolve ) => { on_action = resolve; } );
  },
};
